// Mission definitions. One fictional WWII surface action, plus two missions
// lifted from the real USS New Jersey's gunfire logs: the 1968-69 Vietnam
// gunline deployment and the February 1984 Shouf bombardment off Beirut.
// Each mission owns its orders checklist, its scenario setup (enemies,
// terrain, shore targets, radio traffic), its per-frame progression, and its
// win/lose banners. main.js stays a dumb frame loop.

import * as THREE from "three";
import { KTS } from "./const.js";
import { spawnSquadron, spawnNightRaid } from "./enemy.js";
import { makeTerrain } from "./terrain.js";
import { ShoreTarget } from "./shore.js";

const A = (x, y, z) => new THREE.Vector3(x, y, z);
const BRIDGE = A(149, 23, 17.5);
const RADAR = A(152.5, 17, 13.5);
const DIRECTOR = A(148.5, 31, 17.5);
const PLOT = A(134.5, 8, 10.5);

const deg = (b) => String(Math.round((((b * 180 / Math.PI) % 360) + 360) % 360)).padStart(3, "0");

// shared order fragments -----------------------------------------------------
const ordBridge = () => ({
  id: "bridge", text: "Take the conn — lay up to the navigation bridge",
  anchor: BRIDGE, label: "NAV BRIDGE",
  check: (G) => G.roomAt(G.player.pos)?.key === "bridge",
});
const ordDesignate = (shore) => ({
  id: "designate",
  // deck names here must match what the T menu and the HUD call the same
  // space — the CIC sits on the 01 level in this hull, so say 01 level
  text: shore ? "Answer the call for fire — designate the target at CIC (T teleports)"
              : "Enemy squadron inbound — designate a target (CIC — SG radar, 01 level)",
  anchor: RADAR, label: "CIC — SG RADAR",
  check: (G) => shore ? !!G.fc.target?.shore : !!G.fc.target,
});
const ordSolution = () => ({
  id: "solution", text: "Work the solution — match the rates on the Mk 8 rangekeeper in plot (T teleports)",
  anchor: PLOT, label: "MAIN BATTERY PLOT",
  check: (G) => G.fc.state === "ready" || G.fc.salvosFired > 0,
});
const ordSalvo = () => ({
  id: "salvo", text: "Commence fire with the main battery (F at plot / bridge / director)",
  anchor: null,
  check: (G) => G.fc.salvosFired > 0,
});

// The spotter walks a target list: calls each one in, waits for the kill,
// reads back battle damage assessment, calls the next.
function makeSpotter(G, callsign, targets) {
  return {
    idx: -1, delay: 6, done: false,
    update(dt) {
      if (this.done || G.over) return;
      const cur = this.idx >= 0 ? targets[this.idx] : null;
      if (cur && !cur.destroyed) return;
      if (cur && cur.destroyed && !cur._bda) {
        cur._bda = true;
        G.hud.log(`${callsign}: TARGET DESTROYED — ${cur.bdaText || "GOOD EFFECT ON TARGET"}. END OF MISSION, OUT.`, "ok");
        G.audio.ding();
        this.delay = 8 + Math.random() * 6;
      }
      this.delay -= dt;
      if (this.delay > 0) return;
      this.idx++;
      const t = targets[this.idx];
      if (!t) { this.done = true; return; }
      if (t.destroyed) return; // already dealt with out of sequence
      this.callIn(t);
    },
    callIn(t) {
      t.called = true;
      const p = G.shipWorldPos();
      const b = Math.atan2(t.pos.x - p.x, t.pos.y - p.z);
      const r = Math.hypot(t.pos.x - p.x, t.pos.y - p.z);
      G.hud.log(`${callsign}: FIRE MISSION — ${t.name}${t.desc ? ", " + t.desc : ""}.`, "bad");
      G.hud.log(`${callsign}: TARGET BEARS ${deg(b)}, RANGE ${(r / 1000).toFixed(1)} KM. DESIGNATE AT CIC WHEN READY.`);
      G.audio.buzz();
    },
  };
}

function shoreProgress(G, order, targets) {
  const done = targets.filter((t) => t.destroyed).length;
  G.hud.setOrderText(order, `Destroy the assigned targets (${done}/${targets.length})`);
  return done === targets.length;
}

// ---------------------------------------------------------------------------
export const MISSIONS = [
  {
    id: "surface",
    title: "SURFACE ACTION",
    tag: "THE PACIFIC, 1944 — WHAT-IF",
    par: 14,
    blurb: "A hostile cruiser squadron is closing over the horizon. Radar picks them up "
      + "at 26 km; nine 16-inch rifles answer. The gun duel the Iowas trained for and never got.",
    ordersTitle: "CAPTAIN'S NIGHT ORDERS",
    orders() {
      return [ordBridge(), ordDesignate(false), ordSolution(), ordSalvo(),
        { id: "sink", text: "Sink the enemy squadron", anchor: null }];
    },
    setup(G) {
      this.t = 0; this.cic = false; this.gq = false;
      G.enemyShips.push(...spawnSquadron(G));
      G.hud.log("UNDERWAY ON COURSE 020, AHEAD 2/3. WELCOME ABOARD, SAILOR.");
    },
    update(G, dt) {
      this.t += dt;
      if (!this.cic && this.t > 9) {
        this.cic = true;
        G.hud.log("CIC: SG RADAR CONTACTS BEARING NORTHEAST — SQUADRON, CLOSING", "bad");
      }
      if (!this.gq && this.t > 16) {
        this.gq = true;
        if (!G.gq) G.soundGQ(true, true);
      }
      const es = G.enemyShips;
      if (es.length && es.every((e) => e.sunk) && !G.over) {
        G.hud.completeOrder("sink");
        G.gameOver(true, null, "ENEMY SQUADRON DESTROYED");
      }
    },
  },

  // ---------------------------------------------------------------------------
  {
    id: "night",
    title: "NIGHT ACTION",
    tag: "THE SLOT, 1944 — WHAT-IF",
    par: 13,
    blurb: "A cruiser-destroyer force comes down The Slot in the dark. Radar sees what "
      + "eyes can't, star shells buy you thirty seconds of noon at a time — and the "
      + "destroyers will be at torpedo range before anybody hears a thing.",
    ordersTitle: "CAPTAIN'S NIGHT ORDERS — DARKEN SHIP",
    orders() {
      return [ordBridge(), ordDesignate(false), ordSolution(),
        { id: "illum", text: "Get illumination up — star shells over the target (I)", anchor: null,
          check: (G) => G.illum.flares.length > 0 },
        ordSalvo(),
        { id: "torps", text: "Evade the torpedo attack — comb the tracks", anchor: null,
          check: (G) => G.enemyShips.filter((e) => e.kind === "dd").every((e) => e.torpsAway || e.sunk)
            && G.torps.fish.length === 0 && G.damage.torpedoFloods === 0 && G.fc.salvosFired > 0 },
        { id: "sink", text: "Break up the raid — sink them all", anchor: null }];
    },
    setup(G) {
      this.t = 0; this.cic = false; this.gq = false;
      G.setNight();
      const s = G.shipState;
      s.heading = 5.5; s.speed = 12 * KTS; s.orderedSpeed = 12 * KTS;
      G.enemyShips.push(...spawnNightRaid(G));
      G.hud.log("DARKEN SHIP. THE SMOKING LAMP IS OUT TOPSIDE. CONDITION TWO WATCHES SET.");
    },
    update(G, dt) {
      this.t += dt;
      if (!this.cic && this.t > 7) {
        this.cic = true;
        G.hud.log("CIC: SG RADAR — MULTIPLE CONTACTS NORTHWEST, 19,000 M, CLOSING FAST. NO VISUAL.", "bad");
      }
      if (!this.gq && this.t > 13) {
        this.gq = true;
        if (!G.gq) G.soundGQ(true, true);
        G.hud.log("CAPTAIN: RADAR HOLDS THEM, EYES DON'T. STAR SHELLS WHEN THE BATTERY'S READY (I).");
      }
      const es = G.enemyShips;
      if (es.length && es.every((e) => e.sunk) && !G.over) {
        G.hud.completeOrder("sink");
        G.gameOver(true, null, "THE RAID IS BROKEN");
      }
    },
  },

  // ---------------------------------------------------------------------------
  {
    id: "korea",
    title: "SIEGE OF WONSAN",
    tag: "KOREA, MAY 1951 — HISTORICAL",
    par: 16,
    blurb: "New Jersey's first war after the mothballs: the longest naval siege of the "
      + "century. Rail lines and gun caves above Wonsan harbor — and the Kalmagak "
      + "batteries that hit her on 21 May, the only combat casualties she ever took.",
    ordersTitle: "FIRE SUPPORT TASKING — TASK FORCE 95",
    orders() {
      return [ordBridge(), ordDesignate(true), ordSalvo(),
        { id: "targets", text: "Destroy the assigned targets (0/4)", anchor: null },
        { id: "cb", text: "Silence the Kalmagak batteries", anchor: null }];
    },
    setup(G) {
      G.terrain = makeTerrain(G.scene, "korea", { dir: 1, coastX: 6000 });
      const s = G.shipState;
      s.heading = 5.8; s.speed = 8 * KTS; s.orderedSpeed = 8 * KTS; // working the harbor arc
      const mk = (o) => { const t = new ShoreTarget(G, o); G.shore.push(t); return t; };
      this.rail = mk({ kind: "dump", name: "RAIL JUNCTION — WONSAN LINE", desc: "ROLLING STOCK AND TRACKAGE FEEDING THE FRONT",
        x: 9800, z: 3400, bdaText: "TRACKS CUT, CARS BURNING" });
      this.caves = mk({ kind: "bunker", name: "GUN CAVES — KALMA PENINSULA", desc: "GUNS FIRING FROM CAVE MOUTHS, DUG INTO THE RIDGE",
        x: 11400, z: -2800, bdaText: "CAVE MOUTHS COLLAPSED" });
      this.supply = mk({ kind: "dump", name: "SUPPLY POINT — HODO PANDO", desc: "STORES STACKED ON THE NECK OF THE PENINSULA",
        x: 8900, z: -6200, bdaText: "SECONDARIES, FIRES SPREADING" });
      this.kalmagak = mk({ kind: "coastal", name: "SHORE BATTERY — KALMAGAK", desc: "152MM GUNS, WELL SERVED AND PATIENT",
        x: 8600, z: 1200, shoots: true, guns: 3, gunRange: 23000, bdaText: "KALMAGAK SILENT" });
      // they had been registering on the anchorage for weeks — their first
      // salvos arrive knowing the range, as they historically did
      this.kalmagak.trackTime = 150;
      this.all = [this.rail, this.caves, this.supply, this.kalmagak];
      this.spotter = makeSpotter(G, "AIR SPOT 27", [this.rail, this.caves, this.supply]);
      this.cbCalled = false;
      this.hitLogged = false;
      G.hud.log("WONSAN HARBOR, MAY 1951. THE SIEGE GUNS TRADE EVERY DAY HERE.");
      G.hud.log("AIR SPOT 27: NEW JERSEY, TARGETS ON THE RAIL LINE AND THE PENINSULA. STAND BY.");
    },
    update(G, dt) {
      this.spotter.update(dt);
      // Kalmagak unmasks after our first salvo — and it shoots like it means it
      if (!this.cbCalled && G.fc.salvosFired > 0) {
        this.cbCalled = true;
        this.kalmagak.holdFire = false;
        setTimeout(() => {
          if (G.over) return;
          G.hud.log("MUZZLE FLASHES ON KALMAGAK — THEY'VE BEEN WAITING FOR US TO ANCHOR OUR ATTENTION", "bad");
          if (!G.gq) G.soundGQ(true, true);
        }, 2500);
      }
      // the hit she really took, if their gunners get lucky here too
      if (!this.hitLogged && G.damage.hitsTaken > 0) {
        this.hitLogged = true;
        G.hud.log("HIT AFT — CORPSMAN LAY AFT ON THE DOUBLE. FIGHT THE SHIP.", "bad");
      }
      if (shoreProgress(G, "targets", this.all) && !G.over) {
        G.hud.completeOrder("targets");
        G.gameOver(true, null, "WONSAN TASKING COMPLETE");
      }
      if (this.kalmagak.destroyed) G.hud.completeOrder("cb");
    },
  },

  // ---------------------------------------------------------------------------
  {
    id: "vietnam",
    title: "THE GUNLINE",
    tag: "VIETNAM, 1969 — HISTORICAL",
    par: 16,
    blurb: "New Jersey's real war: naval gunfire support off the DMZ, 1968-69. An ANGLICO "
      + "spotter calls fire missions onto NVA positions inland — and the coastal guns "
      + "that took her under fire at Cap Mui Lay shoot back.",
    ordersTitle: "FIRE SUPPORT TASKING — SEA DRAGON",
    orders() {
      return [ordBridge(), ordDesignate(true), ordSalvo(),
        { id: "targets", text: "Destroy the assigned targets (0/4)", anchor: null },
        { id: "cb", text: "Silence the counter-battery fire from Cap Mui Lay", anchor: null }];
    },
    setup(G) {
      G.terrain = makeTerrain(G.scene, "vietnam", { dir: -1, coastX: 7000 });
      const s = G.shipState;
      s.heading = 6.1; s.speed = 8 * KTS; s.orderedSpeed = 8 * KTS; // slow NNW along the coast
      const mk = (o) => { const t = new ShoreTarget(G, o); G.shore.push(t); return t; };
      this.arty = mk({ kind: "battery", name: "NVA 122MM BATTERY", desc: "FIRING ON FRIENDLIES SOUTH OF THE RIVER",
        x: -10200, z: -1600, bdaText: "GUNS SILENT, PITS BURNING" });
      this.dump = mk({ kind: "dump", name: "SUPPLY DUMP", desc: "TRUCKS AND STACKED ORDNANCE UNDER CAMOUFLAGE",
        x: -12600, z: 2400, bdaText: "SECONDARY EXPLOSIONS, LARGE FIRES" });
      this.bunker = mk({ kind: "bunker", name: "BUNKER COMPLEX", desc: "REINFORCED FIGHTING POSITIONS ON THE RIDGE",
        x: -15800, z: -5200, bdaText: "BUNKERS CAVED IN, POSITION ABANDONED" });
      this.coastal = mk({ kind: "coastal", name: "COASTAL BATTERY — CAP MUI LAY", desc: "130MM GUNS IN REVETMENTS",
        x: -9400, z: 7800, shoots: true, gunRange: 21000, bdaText: "BATTERY SILENCED" });
      this.all = [this.arty, this.dump, this.bunker, this.coastal];
      this.spotter = makeSpotter(G, "LEATHERNECK 2-6", [this.arty, this.dump, this.bunker]);
      this.cbCalled = false;
      G.hud.log("ON THE GUNLINE OFF THE DMZ. SPOT TEAM UP ON THE NGFS NET.");
      G.hud.log("LEATHERNECK 2-6: NEW JERSEY, STAND BY FOR FIRE MISSIONS.");
    },
    update(G, dt) {
      this.spotter.update(dt);
      // the coastal guns unmask after our first salvo
      if (!this.cbCalled && G.fc.salvosFired > 0) {
        this.coastal.holdFire = false;
        if (this.coastal.trackTime === 0) this.coastal.trackTime = 0.01;
      }
      if (!this.cbCalled && !this.coastal.holdFire && this.coastal.trackTime > 0 && !this.coastal.called) {
        this.cbCalled = true;
        this.coastal.called = true;
        setTimeout(() => {
          if (G.over) return;
          G.hud.log("CIC: COUNTER-BATTERY RADAR PLOTS THE FLASHES — CAP MUI LAY, PASSED TO PLOT AS A PRIORITY TARGET", "bad");
          if (!G.gq) G.soundGQ(true, true);
        }, 3000);
      }
      if (shoreProgress(G, "targets", this.all) && !G.over) {
        G.hud.completeOrder("targets");
        G.gameOver(true, null, "NAVAL GUNFIRE SUPPORT COMPLETE");
      }
      if (this.coastal.destroyed) G.hud.completeOrder("cb");
    },
  },

  // ---------------------------------------------------------------------------
  {
    id: "lebanon",
    title: "BEIRUT STATION",
    tag: "LEBANON, FEBRUARY 1984 — HISTORICAL",
    par: 16,
    blurb: "Off Beirut with the multinational force ashore. Artillery in the Shouf "
      + "mountains is shelling the city below. Sixteen-inch fire into the high batteries — "
      + "with the city under your line of fire, every round must be clean.",
    ordersTitle: "FIRE SUPPORT TASKING — BEIRUT",
    orders() {
      return [ordBridge(), ordDesignate(true), ordSalvo(),
        { id: "targets", text: "Destroy the assigned targets (0/4)", anchor: null },
        { id: "roe", text: "ROE: no rounds in the city (3 strikes relieves you of command)", anchor: null }];
    },
    setup(G) {
      G.terrain = makeTerrain(G.scene, "lebanon", { dir: 1, coastX: 6000 });
      const s = G.shipState;
      s.heading = 0.2; s.speed = 5 * KTS; s.orderedSpeed = 5 * KTS;
      const mk = (o) => { const t = new ShoreTarget(G, o); G.shore.push(t); return t; };
      this.targets = [
        mk({ kind: "battery", name: "GUN POSITION — UPPER SHOUF", desc: "FIRING INTO THE CITY",
          x: 15200, z: 2600, bdaText: "POSITION DESTROYED, RIDGE QUIET" }),
        mk({ kind: "battery", name: "ARTILLERY BATTERY — SUQ AL GHARB RIDGE", desc: "DUG-IN 130MM GUNS",
          x: 13600, z: -2400, shoots: true, guns: 1, gunRange: 24000, bdaText: "GUNS DESTROYED" }),
        mk({ kind: "bunker", name: "OBSERVATION BUNKER", desc: "DIRECTING FIRE ONTO THE AIRPORT",
          x: 12200, z: 5400, bdaText: "OP GONE — SPOTTING NET WENT SILENT" }),
        mk({ kind: "dump", name: "AMMUNITION POINT", desc: "SHELLS STACKED BEHIND THE CREST",
          x: 16400, z: -5600, bdaText: "COOKING OFF — FIRES ON THE MOUNTAIN" }),
      ];
      this.spotter = makeSpotter(G, "FIRE SUPPORT COORDINATION CENTER", this.targets);
      this.cityHits = 0;
      this.harassT = 90;
      G.hud.log("ON STATION OFF BEIRUT. THE SHOUF IS FIRING ON THE CITY AGAIN.");
      G.hud.log("FSCC: NEW JERSEY, TARGETS IN THE HIGH BATTERIES TO FOLLOW. MIND YOUR PLOT — THE CITY IS UNDER YOUR GUN-TARGET LINE.");
    },
    onCityHit(G) {
      this.cityHits++;
      G.hud.setOrderText("roe", `ROE: no rounds in the city (${this.cityHits}/3 strikes)`);
      if (this.cityHits >= 3) {
        G.gameOver(false, "Three rounds into the city. You are relieved of command.", "CHECK FIRE — CHECK FIRE");
      } else {
        G.hud.log(`FSCC: CHECK YOUR PLOT — ROUNDS IN THE CITY. ${3 - this.cityHits} MORE AND YOU ARE DONE FIRING.`, "bad");
        G.audio.buzz();
      }
    },
    update(G, dt) {
      this.spotter.update(dt);
      // sporadic, badly-aimed harassing fire from the ridge battery
      this.harassT -= dt;
      if (this.harassT < 0) {
        this.harassT = 1e9;
        const ridge = this.targets[1];
        if (!ridge.destroyed) {
          ridge.holdFire = false;
          G.hud.log("FSCC: THE RIDGE BATTERY IS TRAINING TOWARD THE FORCE OFFSHORE — WEAPONS FREE ON IT WHEN CALLED.", "bad");
        }
      }
      if (shoreProgress(G, "targets", this.targets) && !G.over) {
        G.hud.completeOrder("targets");
        G.gameOver(true, null, "THE MOUNTAIN GUNS ARE SILENT");
      }
    },
  },
];
